import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import {
    AMMO_REASONS, PAYMENT_METHODS, isRevenueReason, todayIso,
} from '../../lib/ammoReasons';

function formatBirth(birth) {
    if (!birth) {
        return '';
    }
    const d = new Date(birth);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function initialState({ isNew, sale }) {
    if (isNew || !sale) {
        return {
            buyer: null,
            memberSearch: '',
            memberHighlight: 0,
            showMembers: true,
            productId: '',
            qty: '',
            reason: 'sale',
            paymentMethod: '',
            comment: '',
            saleDate: todayIso(),
            error: '',
            isSaving: false,
        };
    }
    const visitor = (sale.visitors || [])[0] || {};
    const line = (sale.products || []).find(p => p && p.isAmmo) || (sale.products || [])[0] || {};
    return {
        buyer: {
            id: visitor.user, firstname: visitor.firstname, lastname: visitor.lastname, birth: visitor.birth,
        },
        memberSearch: '',
        memberHighlight: 0,
        showMembers: true,
        productId: line.id ? String(line.id) : '',
        qty: line.qty || '',
        reason: sale.ammoReason || 'sale',
        paymentMethod: sale.paymentMethod || '',
        comment: sale.description || '',
        saleDate: sale.saleDate ? String(sale.saleDate).slice(0, 10) : todayIso(),
        error: '',
        isSaving: false,
    };
}

@observer
class AmmoSaleDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = initialState(props);
        this.searchTimer = null;
    }

    componentDidMount() {
        const { ammoSaleStore } = this.props.stores;
        ammoSaleStore.loadProducts();
    }

    componentWillUnmount() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
    }

    onField = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, error: '' });
    }

    onMemberSearch = (e) => {
        const { value } = e.target;
        const { ammoSaleStore } = this.props.stores;
        const { searchDelayMs = 300 } = this.props;
        this.setState({
            memberSearch: value, buyer: null, memberHighlight: 0, showMembers: true,
        });
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => {
            ammoSaleStore.searchMembers(value);
        }, searchDelayMs);
    }

    // Same keyboard pattern as the visitor search in
    // dyrejournal/phoneConsultationEdit.js: arrows move the highlight and wrap,
    // Enter picks the highlighted member, Escape hides the list.
    onMemberKeyDown = (e) => {
        const { ammoSaleStore } = this.props.stores;
        const { members = [] } = ammoSaleStore;
        const { memberHighlight, memberSearch, showMembers } = this.state;
        if (!members.length || memberSearch.length < 2 || !showMembers) {
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ memberHighlight: (memberHighlight + 1) % members.length });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ memberHighlight: (memberHighlight - 1 + members.length) % members.length });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const member = members[memberHighlight] || members[0];
            if (member) {
                this.pickMember(member, { focusNext: true });
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.setState({ showMembers: false, memberHighlight: 0 });
        }
    }

    pickMember = (member, { focusNext = false } = {}) => {
        this.setState({ buyer: member, memberSearch: '', memberHighlight: 0 }, () => {
            // Only on keyboard pick: the search input is gone after render, so
            // move focus on to the next field. Not on click, so a tap never
            // pops the native select picker on phones.
            if (focusNext && this.productSelect) {
                this.productSelect.focus();
            }
        });
    }

    canSave = () => {
        const {
            buyer, productId, qty, reason, paymentMethod, isSaving,
        } = this.state;
        if (isSaving || !buyer?.id || !productId || !(parseInt(qty, 10) > 0)) {
            return false;
        }
        if (isRevenueReason(reason) && !paymentMethod) {
            return false;
        }
        return true;
    }

    save = async () => {
        const { ammoSaleStore, appState } = this.props.stores;
        const {
            isNew, sale, callback = () => {}, drawerLevel = 1,
        } = this.props;
        const {
            buyer, productId, qty, reason, paymentMethod, comment, saleDate,
        } = this.state;
        if (!this.canSave()) {
            return;
        }
        this.setState({ isSaving: true, error: '' });
        const input = {
            userId: buyer.id, productId, qty, reason, paymentMethod, comment, saleDate,
        };
        const response = isNew
            ? await ammoSaleStore.registerSale(input)
            : await ammoSaleStore.updateSale(sale.id, input);
        if (response?.status === 201 || response?.status === 202) {
            callback();
            appState.toggleDrawer(false, drawerLevel);
            return;
        }
        this.setState({ isSaving: false, error: response?.message || 'Could not save' });
    }

    render() {
        const { ammoSaleStore } = this.props.stores;
        const { isNew } = this.props;
        const {
            buyer, memberSearch, memberHighlight, showMembers, productId, qty, reason, paymentMethod, comment, saleDate, error, isSaving,
        } = this.state;
        const { products = [], members = [] } = ammoSaleStore;
        const product = products.find(p => String(p.id) === String(productId));
        const showPayment = isRevenueReason(reason);

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 720px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='px-3'>
                        <h5 class='mb-3'>
                            <i class='fa-solid fa-box-open me-2' />
                            {isNew
                                ? <Text id='ammoSale.register'>Register ammo sale</Text>
                                : <Text id='ammoSale.edit'>Edit ammo sale</Text>}
                        </h5>

                        <div class='mb-3'>
                            <label class='form-label'><Text id='ammoSale.member'>Member</Text></label>
                            {buyer?.id ? (
                                <div class='d-flex align-items-center justify-content-between border rounded p-2'>
                                    <div>
                                        <strong>{buyer.firstname} {buyer.lastname}</strong>
                                        {formatBirth(buyer.birth) ? (
                                            <small class='text-body-secondary ms-2'>
                                                <Text id='ammoSale.born'>Born</Text> {formatBirth(buyer.birth)}
                                            </small>
                                        ) : (
                                            <small class='text-warning ms-2'>
                                                <i class='fa-solid fa-triangle-exclamation me-1' />
                                                <Text id='ammoSale.birth-missing'>Birth date missing on profile</Text>
                                            </small>
                                        )}
                                    </div>
                                    {isNew && (
                                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={() => this.setState({ buyer: null })}>
                                            <Text id='ammoSale.change'>Change</Text>
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div class='position-relative'>
                                    <input
                                        class='form-control'
                                        type='text'
                                        name='memberSearch'
                                        autocomplete='off'
                                        value={memberSearch}
                                        placeholder='Søk etter medlem'
                                        onInput={this.onMemberSearch}
                                        onKeyDown={this.onMemberKeyDown}
                                    />
                                    {members.length > 0 && memberSearch.length >= 2 && showMembers && (
                                        <div class='list-group position-absolute w-100 shadow' style='z-index: 10;'>
                                            {members.map((member, idx) => (
                                                <button
                                                    key={member.id}
                                                    type='button'
                                                    class={`list-group-item list-group-item-action${idx === memberHighlight ? ' active' : ''}`}
                                                    onClick={() => this.pickMember(member)}
                                                    onMouseEnter={() => this.setState({ memberHighlight: idx })}
                                                >
                                                    <strong>{member.firstname} {member.lastname}</strong>
                                                    {formatBirth(member.birth) && <small class={`ms-2${idx === memberHighlight ? '' : ' text-body-secondary'}`}>{formatBirth(member.birth)}</small>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div class='mb-3'>
                            <label class='form-label'><Text id='ammoSale.product'>Ammo product</Text></label>
                            <select
                                class='form-select'
                                name='productId'
                                value={productId}
                                onInput={this.onField}
                                disabled={!isNew}
                                ref={(el) => { this.productSelect = el; }}
                            >
                                <option value=''>-</option>
                                {products.map(p => (
                                    <option key={p.id} value={String(p.id)}>
                                        {p.caliber ? `${p.caliber} - ` : ''}{p.name} ({p.inStock || 0} <Text id='ammoSale.in-stock'>in stock</Text>)
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div class='row'>
                            <div class='col-6 mb-3'>
                                <label class='form-label'><Text id='ammoSale.qty'>Quantity (units)</Text></label>
                                <input class='form-control' type='number' name='qty' min='1' step='1' value={qty} onInput={this.onField} />
                            </div>
                            <div class='col-6 mb-3'>
                                <label class='form-label'><Text id='ammoSale.date'>Date</Text></label>
                                <input class='form-control' type='date' name='saleDate' value={saleDate} onInput={this.onField} />
                            </div>
                        </div>

                        <div class='row'>
                            <div class='col-6 mb-3'>
                                <label class='form-label'><Text id='ammoSale.reason'>Reason</Text></label>
                                <select class='form-select' name='reason' value={reason} onInput={this.onField}>
                                    {AMMO_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                                </select>
                            </div>
                            {showPayment && (
                                <div class='col-6 mb-3'>
                                    <label class='form-label'><Text id='ammoSale.payment'>Payment</Text></label>
                                    <select class='form-select' name='paymentMethod' value={paymentMethod} onInput={this.onField}>
                                        <option value=''>-</option>
                                        {PAYMENT_METHODS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div class='mb-3'>
                            <label class='form-label'><Text id='ammoSale.comment'>Comment</Text></label>
                            <textarea class='form-control' name='comment' rows='2' value={comment} onInput={this.onField} />
                        </div>

                        {product && parseInt(qty, 10) > 0 && (
                            <div class='alert alert-info py-2'>
                                <Text id='ammoSale.total'>Total</Text>: {Math.round((product.priceIncVat || 0) * parseInt(qty, 10) * 100) / 100} kr
                                {!showPayment && <span class='ms-2'>(<Text id='ammoSale.internal-note'>internal use, not revenue</Text>)</span>}
                            </div>
                        )}

                        {error && <div class='alert alert-danger py-2'>{error}</div>}

                        <div class='d-flex justify-content-end'>
                            <button type='button' class='btn btn-primary rounded-pill' disabled={!this.canSave()} onClick={this.save}>
                                {isSaving && <span class='spinner-border spinner-border-sm me-2' />}
                                <i class='fa-solid fa-save me-1' />
                                <Text id='ammoSale.save'>Save</Text>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default AmmoSaleDrawer;
